import classNames from 'classnames';
import React, { memo } from 'react';
import { Button, Gold, Icon, Spinner, TooltipBody } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { API_ERROR_REASONS, ATTRIBUTES_VALIDATION_STATE, CURRENCY } from '~/constants';
import { clanCost } from '~/helpers/clan';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { t } from '~/helpers/localization';
import { useAppSelector } from '~/store';
import { isApiErrorObject } from '~/utils/errors';

import { ErrorDublon } from '~/UIKit';

import styles from './ClanCreate.scss';

import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { RootState } from '~/store';
import type { IApiError } from '~/types/api';

interface FooterProps {
  error: IApiError | null;
  isProcessing: boolean;
  name: IClanCreateStateFields['name'];
  tag: IClanCreateStateFields['tag'];
  onCreateClan: () => void;
}

const ClanCreateFooter: React.FC<FooterProps> = ({ error = null, isProcessing, name, tag, onCreateClan }) => {
  const currentAccount = useAppSelector((s: RootState) => s.currentAccount);

  const cost = clanCost();
  const isDiscount = settings.clanCreate.cost !== cost; // cost already considers discount

  const balanceGold = currentAccount.balance?.[CURRENCY.GOLD] ?? 0;
  const baseNeedGold = Math.max(cost - balanceGold, 0);

  const needGold =
    error && isApiErrorObject(error) && error.additional_info?.reason === API_ERROR_REASONS.NOT_ENOUGH_GOLD
      ? Number(error.additional_info.amount)
      : baseNeedGold;

  const hasClientError = Boolean(tag.error || name.error);
  const hasValues = Boolean(tag.value && name.value);

  const isTagAvailable = tag.available === ATTRIBUTES_VALIDATION_STATE.AVAILABLE;
  const isNameAvailable = name.available === ATTRIBUTES_VALIDATION_STATE.AVAILABLE;
  const isNameAndTagAvailable = isTagAvailable && isNameAvailable;

  let isDisabled = false;
  let tooltipContent: React.ReactNode = null;

  if (error && typeof error === 'object' && error.message) {
    isDisabled = true;
  }

  if (currentAccount.inClanCooldownTill) {
    tooltipContent = (
      <TooltipBody>
        {t('Вы недавно покинули клан. Создать свой клан можно не ранее %(date)s, %(time)s', {
          date: isoToFormattedLocalDate(currentAccount.inClanCooldownTill),
          time: isoToFormattedLocalTime(currentAccount.inClanCooldownTill),
        })}
      </TooltipBody>
    );

    return (
      <>
        <div className={styles.priceLabel}>{t('Стоимость')}:</div>
        <div className={styles.price}>
          <Gold className={styles.priceFinal} amount={cost} />
        </div>
        <Button isFlat isDisabled tooltipContent={tooltipContent}>
          {t('Создать клан:кнопка создания клана')}
        </Button>
      </>
    );
  }

  if (needGold > 0) {
    isDisabled = true;
    tooltipContent = (
      <TooltipBody>
        <ErrorDublon amount={cost} deficit={needGold} />
      </TooltipBody>
    );
  } else if (hasClientError || !hasValues) {
    isDisabled = true;
    tooltipContent = (
      <TooltipBody>{t('Введите уникальные тег и название, удовлетворяющие Правилам кланов.')}</TooltipBody>
    );
  } else if (!isNameAndTagAvailable) {
    isDisabled = true;
    tooltipContent = <TooltipBody>{t('Клан с таким тегом или названием уже зарегистрирован.')}</TooltipBody>;
  } else if (isDiscount) {
    tooltipContent = (
      <TooltipBody>
        <div className={styles.discountTooltip}>
          <div className={styles.discountTooltipHead}>
            <Icon glyph="discount" />
            {t('Акция:tooltip')}
          </div>
          <div>
            <span className={styles.discountTooltipPriceText}>{t('Стоимость покупки:tooltip')}</span>
            <Gold amount={cost} />
          </div>
          <div className={styles.discountTooltipPriceCostWrap}>
            <span className={styles.discountTooltipPriceCost}>{settings.clanCreate.cost}</span>
          </div>
        </div>
      </TooltipBody>
    );
  }

  return (
    <>
      <div className={styles.priceLabel}>{t('Стоимость')}:</div>
      <div className={styles.price}>
        {isDiscount && <div className={styles.priceDiscounted}>{settings.clanCreate.cost}</div>}
        <Gold className={styles.priceFinal} amount={cost} />
      </div>
      <div className={styles.buttons}>
        {isProcessing && <Spinner className={styles.buttonsLoader} />}
        <Button
          className={classNames({ [styles.buttonIsProcessing]: isProcessing })}
          isFlat
          onClick={onCreateClan}
          isDisabled={isDisabled || isProcessing}
          tooltipContent={tooltipContent}
          key={`create_clan_button_${name.value}_${tag.value}_${isDisabled || isProcessing}`}
        >
          {t('Создать клан:кнопка создания клана')}
        </Button>
      </div>
    </>
  );
};

export default memo(ClanCreateFooter);
